import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Menu, AlertTriangle, Eye, Key, ChevronRight, ChevronDown, Home, Shield, Users, FileText, BarChart3, Package } from 'lucide-react';
import apiService from '../services/apiService';

const MenuManagement = () => {
  const [menus, setMenus] = useState([]);
  const [menuTree, setMenuTree] = useState([]);
  const [filteredMenus, setFilteredMenus] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [menuPermissions, setMenuPermissions] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [error, setError] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    icon: '',
    url: '',
    component: '',
    description: '',
    parent: null,
    sortOrder: 0,
    visible: true,
    active: true,
    menuType: 'MENU',
    permissionIds: []
  });

  const iconOptions = [
    { value: 'Home', label: 'Home', icon: Home },
    { value: 'Shield', label: 'Shield', icon: Shield },
    { value: 'Users', label: 'Users', icon: Users },
    { value: 'Key', label: 'Key', icon: Key },
    { value: 'FileText', label: 'File Text', icon: FileText },
    { value: 'BarChart3', label: 'Bar Chart', icon: BarChart3 },
    { value: 'Package', label: 'Package', icon: Package },
    { value: 'Menu', label: 'Menu', icon: Menu }
  ];

  useEffect(() => {
    loadMenus();
    loadPermissions();
  }, []);

  useEffect(() => {
    filterMenus();
  }, [menus, searchTerm]);

  const loadMenus = async () => {
    try {
      setLoading(true);
      apiService.refreshTokenFromStorage();
      const [menusData, treeData] = await Promise.all([
        apiService.getAllMenus(),
        apiService.getMenuTree()
      ]);
      setMenus(menusData);
      setMenuTree(treeData);
    } catch (error) {
      setError('Failed to load menus');
      console.error('Error loading menus:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      apiService.refreshTokenFromStorage();
      const data = await apiService.getAllPermissions();
      setPermissions(data);
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  };

  const loadMenuPermissions = async (menuId) => {
    try {
      // This would need to be implemented in the backend
      // For now, we'll show a placeholder
      setMenuPermissions([]);
    } catch (error) {
      console.error('Error loading menu permissions:', error);
      setMenuPermissions([]);
    }
  };

  const filterMenus = () => {
    let filtered = [...menus];

    if (searchTerm) {
      filtered = filtered.filter(menu =>
        menu.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        menu.url?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        menu.component?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredMenus(filtered);
  };

  const handleCreate = () => {
    setModalMode('create');
    setSelectedMenu(null);
    setFormData({
      title: '',
      icon: '',
      url: '',
      component: '',
      description: '',
      parent: null,
      sortOrder: 0,
      visible: true,
      active: true,
      menuType: 'MENU',
      permissionIds: []
    });
    setShowModal(true);
  };

  const handleEdit = (menu) => {
    setModalMode('edit');
    setSelectedMenu(menu);
    setFormData({
      title: menu.title,
      icon: menu.icon || '',
      url: menu.url || '',
      component: menu.component || '',
      description: menu.description || '',
      parent: menu.parent,
      sortOrder: menu.sortOrder || 0,
      visible: menu.visible,
      active: menu.active,
      menuType: menu.menuType || 'MENU',
      permissionIds: menu.permissions ? menu.permissions.map(p => p.id) : []
    });
    setShowModal(true);
  };

  const handleView = (menu) => {
    setModalMode('view');
    setSelectedMenu(menu);
    setShowModal(true);
  };

  const handleViewPermissions = async (menu) => {
    setSelectedMenu(menu);
    await loadMenuPermissions(menu.id);
    setShowPermissionsModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const menuData = {
        ...formData
      };

      if (modalMode === 'create') {
        await apiService.createMenu(menuData);
      } else {
        await apiService.updateMenu(selectedMenu.id, menuData);
      }

      setShowModal(false);
      loadMenus();
    } catch (error) {
      setError(error.message || 'Failed to save menu');
    }
  };

  const handleDelete = async (menuId) => {
    if (window.confirm('Are you sure you want to delete this menu? This will also delete all child menus.')) {
      try {
        await apiService.deleteMenu(menuId);
        loadMenus();
      } catch (error) {
        setError('Failed to delete menu');
      }
    }
  };

  const handlePermissionToggle = (permissionId) => {
    const currentPermissions = formData.permissionIds;
    const newPermissions = currentPermissions.includes(permissionId)
      ? currentPermissions.filter(id => id !== permissionId)
      : [...currentPermissions, permissionId];
    
    setFormData({ ...formData, permissionIds: newPermissions });
  };

  const toggleNodeExpansion = (nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  const renderTreeNode = (node, level = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes[node.id];
    const IconComponent = iconOptions.find(opt => opt.value === node.icon)?.icon || Menu;

    return (
      <div key={node.id} className="select-none">
        <div 
          className={`flex items-center py-2 px-3 hover:bg-gray-50 rounded-lg cursor-pointer ${
            level > 0 ? 'ml-' + (level * 6) : ''
          }`}
          style={{ marginLeft: level * 24 }}
        >
          <div className="flex items-center flex-1">
            {hasChildren && (
              <button
                onClick={() => toggleNodeExpansion(node.id)}
                className="mr-2 p-1 hover:bg-gray-200 rounded"
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
            )}
            {!hasChildren && <div className="w-6" />}
            
            <div className="flex-shrink-0 h-8 w-8 mr-3">
              <div className="h-8 w-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                <IconComponent className="h-4 w-4 text-indigo-600" />
              </div>
            </div>
            
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">{node.title}</div>
              <div className="text-xs text-gray-500">{node.component && `${node.component} • `}{node.url || 'No URL'}</div>
            </div>
            
            <div className="flex items-center space-x-1">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                node.active 
                  ? 'text-green-800 bg-green-100' 
                  : 'text-red-800 bg-red-100'
              }`}>
                {node.active ? 'Active' : 'Inactive'}
              </span>
              
              <div className="flex items-center space-x-1 ml-2">
                <button
                  onClick={() => handleView(node)}
                  className="text-indigo-600 hover:text-indigo-900 p-1"
                  title="View Menu"
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleEdit(node)}
                  className="text-blue-600 hover:text-blue-900 p-1"
                  title="Edit Menu"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleViewPermissions(node)}
                  className="text-green-600 hover:text-green-900 p-1"
                  title="View Permissions"
                >
                  <Key className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(node.id)}
                  className="text-red-600 hover:text-red-900 p-1"
                  title="Delete Menu"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {hasChildren && isExpanded && (
          <div>
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const formatDateTime = (dateTime) => {
    return new Date(dateTime).toLocaleString();
  };

  const getParentMenuOptions = () => {
    return menus.filter(menu => menu.id !== selectedMenu?.id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Menu Management</h1>
          <p className="text-gray-600">Manage system navigation menus and access</p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-2">
          <button
            onClick={() => {
              Object.keys(menuTree).forEach(key => {
                setExpandedNodes(prev => ({ ...prev, [key]: true }));
              });
            }}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Expand All
          </button>
          <button
            onClick={handleCreate}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Menu</span>
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search menus..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex items-end">
            <button
              onClick={() => setSearchTerm('')}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Clear Search
            </button>
          </div>
        </div>
      </div>

      {/* Menu Tree */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Menu Hierarchy</h3>
          {menuTree.length > 0 ? (
            <div className="space-y-2">
              {menuTree.map(node => renderTreeNode(node))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Menu className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No menus found</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating a new menu.</p>
            </div>
          )}
        </div>
      </div>

      {/* Menu Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {modalMode === 'create' ? 'Add New Menu' : modalMode === 'edit' ? 'Edit Menu' : 'Menu Details'}
                    </h3>
                  </div>

                  {modalMode === 'view' ? (
                    // View Mode
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Menu Title</label>
                          <p className="mt-1 text-sm text-gray-900">{selectedMenu?.title}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Component</label>
                          <p className="mt-1 text-sm text-gray-900">{selectedMenu?.component || 'No component'}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">URL</label>
                          <p className="mt-1 text-sm text-gray-900">{selectedMenu?.url || 'No URL'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Icon</label>
                          <p className="mt-1 text-sm text-gray-900">{selectedMenu?.icon || 'No Icon'}</p>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedMenu?.description || 'No description'}</p>
                      </div>
                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Sort Order</label>
                          <p className="mt-1 text-sm text-gray-900">{selectedMenu?.sortOrder || 0}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Menu Type</label>
                          <p className="mt-1 text-sm text-gray-900">{selectedMenu?.menuType || 'MENU'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Active</label>
                          <p className="mt-1 text-sm text-gray-900">{selectedMenu?.active ? 'Yes' : 'No'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Visible</label>
                          <p className="mt-1 text-sm text-gray-900">{selectedMenu?.visible ? 'Yes' : 'No'}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Create/Edit Mode
                    <div className="space-y-6">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Menu Title *</label>
                          <input
                            type="text"
                            required
                            value={formData.title}
                            onChange={(e) => setFormData({...formData, title: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            placeholder="e.g., User Management"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Component</label>
                          <input
                            type="text"
                            value={formData.component}
                            onChange={(e) => setFormData({...formData, component: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            placeholder="e.g., UserManagement"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                          <input
                            type="text"
                            value={formData.url}
                            onChange={(e) => setFormData({...formData, url: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            placeholder="e.g., /users"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                          <select
                            value={formData.icon}
                            onChange={(e) => setFormData({...formData, icon: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          >
                            <option value="">Select an icon</option>
                            {iconOptions.map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          rows={3}
                          value={formData.description}
                          onChange={(e) => setFormData({...formData, description: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="Describe this menu item..."
                        />
                      </div>

                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Parent Menu</label>
                          <select
                            value={formData.parent?.id || ''}
                            onChange={(e) => {
                              const parent = menus.find(m => m.id.toString() === e.target.value);
                              setFormData({...formData, parent: parent || null});
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          >
                            <option value="">No Parent (Root Menu)</option>
                            {getParentMenuOptions().map(menu => (
                              <option key={menu.id} value={menu.id}>{menu.title}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                          <input
                            type="number"
                            min="0"
                            value={formData.sortOrder}
                            onChange={(e) => setFormData({...formData, sortOrder: parseInt(e.target.value) || 0})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Menu Type</label>
                          <select
                            value={formData.menuType}
                            onChange={(e) => setFormData({...formData, menuType: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          >
                            <option value="MENU">MENU</option>
                            <option value="SEPARATOR">SEPARATOR</option>
                            <option value="HEADER">HEADER</option>
                            <option value="LINK">LINK</option>
                            <option value="ACTION">ACTION</option>
                          </select>
                        </div>
                        <div className="flex flex-col space-y-3 pt-6">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.active}
                              onChange={(e) => setFormData({...formData, active: e.target.checked})}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-900">Active</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.visible}
                              onChange={(e) => setFormData({...formData, visible: e.target.checked})}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-900">Visible</span>
                          </label>
                        </div>
                      </div>

                      {/* Permissions Section */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-4">Required Permissions</label>
                        <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-4">
                          <div className="grid grid-cols-2 gap-2">
                            {permissions.map(permission => (
                              <label key={permission.id} className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={formData.permissionIds.includes(permission.id)}
                                  onChange={() => handlePermissionToggle(permission.id)}
                                  className="h-3 w-3 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-xs text-gray-700">{permission.displayName}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  {modalMode !== 'view' && (
                    <button
                      type="submit"
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      {modalMode === 'create' ? 'Create Menu' : 'Update Menu'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {modalMode === 'view' ? 'Close' : 'Cancel'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {showPermissionsModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Menu Permissions - {selectedMenu?.title}
                  </h3>
                  <p className="text-sm text-gray-600">Permissions required to access this menu</p>
                </div>

                <div className="space-y-4">
                  {menuPermissions.length > 0 ? (
                    <div className="grid grid-cols-2 gap-4">
                      {menuPermissions.map(permission => (
                        <div key={permission.id} className="flex items-center border border-gray-200 rounded-lg p-3">
                          <Key className="h-4 w-4 text-indigo-500 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{permission.displayName}</div>
                            <div className="text-xs text-gray-500">{permission.module}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Key className="mx-auto h-8 w-8 text-gray-400" />
                      <p className="mt-2 text-sm text-gray-500">No permissions required for this menu</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setShowPermissionsModal(false)}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuManagement;